import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Offcanvas, Nav, Navbar, Button } from 'react-bootstrap'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import Footer from '../components/Footer'
import Swal from 'sweetalert2'
import axios from 'axios'

export default function MainLayout() {
  const [showSidebar, setShowSidebar] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  const handleClose = () => setShowSidebar(false)
  const handleShow = () => setShowSidebar(true)

  const isActive = (path) => location.pathname === path

  const navigateAndClose = (path) => {
    navigate(path)
    handleClose()
  }

  // 🔥 CRITICAL: Check token on mount and protect the layout
  useEffect(() => {
    console.log('=== MainLayout Mounted ===');
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    console.log('MainLayout - Token:', token ? '✅ Present' : '❌ Missing');

    if (!token) {
      console.log('No token in MainLayout, redirecting to login');
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  // 🆕 Handle navigation with Ctrl+Click support
  const handleNavigation = (e, path) => {
    // Check if Ctrl key is pressed or middle mouse button
    if (e.ctrlKey || e.metaKey || e.button === 1) {
      // Open in new tab
      e.preventDefault();
      window.open(path, '_blank');
    } else {
      // Normal navigation
      navigate(path);
    }
  };

  // 🆕 Handle mobile navigation with Ctrl+Click support
  const handleMobileNavigation = (e, path) => {
    // For mobile, we want to navigate in the same tab
    // Ctrl+Click on mobile doesn't work the same way
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(path, '_blank');
    } else {
      navigateAndClose(path);
    }
  };

  const handleLogout = async () => {
    const token = localStorage.getItem('token') || sessionStorage.getItem('token');

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you really want to log out?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Logout',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      try {
        const response = await axios.get('https://codydealms.codydeasoftech.in/api/teacher/logout', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        Swal.fire({
          icon: 'success',
          title: 'Logged out',
          text: 'You have been successfully logged out',
          timer: 2000,
          showConfirmButton: false
        });

        // Clear all storages
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        localStorage.removeItem('rememberMe');

        navigate('/login', { replace: true });
      } catch (error) {
        console.error('Logout failed:', error);
        Swal.fire({
          icon: 'error',
          title: 'Logout Failed',
          text: 'Something went wrong. Please try again.',
        });
      }
    }
  };

  return (
    <>
      {/* Sticky Header/Navbar */}
      <Navbar bg="primary" variant="dark" expand="lg" className="px-3 fixed-top shadow-sm">
        <Button className="me-2 d-lg-none" onClick={handleShow}>
          <i className="fa-solid fa-bars"></i>
        </Button>

        <Navbar.Brand className="d-flex align-items-center gap-2">
          <img
            src="/newlmslogo_webportal.png"
            alt="Logo"
            style={{ height: "36px", width: "52px", objectFit: "contain" }}
          />
          Barasat College
        </Navbar.Brand>
      </Navbar>

      {/* Offcanvas Sidebar for mobile */}
      <Offcanvas show={showSidebar} onHide={handleClose} backdrop="static">
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Menu</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/dashboard')}
              onMouseDown={(e) => {
                // Prevent default for middle click
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-house me-3"></i> Dashboard
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/study-materials')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-book me-3"></i> Study Materials
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/useful-links')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-link me-3"></i> Useful Links
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/useful-videos')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-brands fa-youtube me-3"></i> Useful Videos
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/exam')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-clipboard-list me-3"></i> Exam List
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/exam-paper')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-book-open me-3"></i> Exam Papers
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/assignments')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-file-pen me-3"></i> Assignments
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/profile')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-user me-3"></i> Profile
            </Nav.Link>
            <Nav.Link
              onClick={(e) => handleMobileNavigation(e, '/change-password')}
              onMouseDown={(e) => {
                if (e.button === 1) e.preventDefault();
              }}
            >
              <i className="fa-solid fa-key me-3"></i> Change Password
            </Nav.Link>
            <Nav.Link onClick={handleLogout}>
              <i className="fa-solid fa-arrow-right-from-bracket me-3"></i> Logout
            </Nav.Link>
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      <Container fluid>
        <Row>
          {/* Desktop Sidebar */}
          <Col
            lg={2}
            className="d-none d-lg-block position-fixed top-0 start-0 vh-100 bg-dark border-end p-3 overflow-auto"
            style={{ marginTop: '56px' }}
          >
            <Nav className="flex-column">
              <Nav.Link
                className={`py-2 ${isActive('/dashboard') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/dashboard')}
                onMouseDown={(e) => {
                  // Prevent default for middle click
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-house me-3"></i> Dashboard
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/study-materials') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/study-materials')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-book me-3"></i> Study Materials
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/useful-links') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/useful-links')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-link me-3"></i> Useful Links
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/useful-videos') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/useful-videos')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-brands fa-youtube me-3"></i> Useful Videos
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/exam') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/exam')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-clipboard-list me-3"></i> Exam List
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/exam-paper') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/exam-paper')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-book-open me-3"></i> Exam Papers
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/assignments') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/assignments')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-file-pen me-3"></i> Assignments
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/profile') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/profile')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-user me-3"></i> Profile
              </Nav.Link>

              <Nav.Link
                className={`py-2 ${isActive('/change-password') ? 'bg-white text-dark fw-bold' : 'text-white'}`}
                onClick={(e) => handleNavigation(e, '/change-password')}
                onMouseDown={(e) => {
                  if (e.button === 1) e.preventDefault();
                }}
              >
                <i className="fa-solid fa-key me-3"></i> Change Password
              </Nav.Link>

              <Nav.Link
                className={`py-2 text-white`}
                onClick={handleLogout}
              >
                <i className="fa-solid fa-arrow-right-from-bracket me-3"></i> Logout
              </Nav.Link>
            </Nav>
          </Col>

          {/* Main Content */}
          <Col
            xs={12}
            lg={{ span: 10, offset: 2 }}
            className="d-flex flex-column bg-light min-vh-100 p-3 pb-0"
            style={{ paddingTop: '70px' }}
          >
            <div className="flex-grow-1 pb-5 mt-5">
              <Outlet />
            </div>
            <Footer />
          </Col>
        </Row>
      </Container>
    </>
  )
}
